import { useEffect, useRef, useState } from 'react';

type UseTimedSequenceParams = {
  itemsCount: number;
  getItemDurationMs: (index: number) => number;
  exitDurationMs?: number;
  onFinished?: () => void;
};

const firstItemIndex = 0;

export const useTimedSequence = ({
  itemsCount,
  getItemDurationMs,
  exitDurationMs = 0,
  onFinished,
}: UseTimedSequenceParams) => {
  const [currentIndex, setCurrentIndex] = useState(firstItemIndex);
  const [leavingIndex, setLeavingIndex] = useState<number | null>(null);

  /* Timers read callbacks through refs so a parent re-render never restarts the running item. */
  const getItemDurationMsRef = useRef(getItemDurationMs);
  const onFinishedRef = useRef(onFinished);

  useEffect(() => {
    getItemDurationMsRef.current = getItemDurationMs;
    onFinishedRef.current = onFinished;
  }, [getItemDurationMs, onFinished]);

  const isLastItem = currentIndex >= itemsCount - 1;
  const hasFinishedHandler = !!onFinished;

  useEffect(() => {
    if (itemsCount === 0) return;
    if (isLastItem && !hasFinishedHandler) return;

    const timeoutId = setTimeout(() => {
      if (isLastItem) {
        onFinishedRef.current?.();
        return;
      }

      setLeavingIndex(currentIndex);
      setCurrentIndex(currentIndex + 1);
    }, getItemDurationMsRef.current(currentIndex));

    return () => clearTimeout(timeoutId);
  }, [currentIndex, isLastItem, itemsCount, hasFinishedHandler]);

  useEffect(() => {
    if (leavingIndex === null) return;

    const timeoutId = setTimeout(() => setLeavingIndex(null), exitDurationMs);

    return () => clearTimeout(timeoutId);
  }, [leavingIndex, exitDurationMs]);

  return { currentIndex, leavingIndex };
};
